<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.schools', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="sm:col-span-1">
                        <div class="flex justify-end space-x-2">
                            <SubmitFormButton
                                :button-text="$t('messages.filter')"
                                :processing="form.processing"
                                is-filter
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton :href="meta.links.create" element="InertiaLink">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.created_at') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.status') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.people', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(school, index) in schools.data"
                                :key="index"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div>
                                        {{ school.name }}
                                    </div>
                                    <span
                                        v-if="school.school_code"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600"
                                        :class="school.status_color_classes"
                                    >
                                        {{ school.school_code }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>{{ school.years }}</div>
                                    <Tippy v-if="school.last_activity_at_ago">
                                        <button>
                                            <div>
                                                {{ $t('messages.last_access') }}: {{ school.last_activity_at_display }}
                                            </div>
                                        </button>
                                        <template #content>
                                            <div>{{ school.last_activity_at_ago }}</div>
                                        </template>
                                    </Tippy>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <span
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                        :class="school.status_color_classes"
                                    >
                                        {{ $t('messages.' + school.status) }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>{{ $t('messages.teachers') }}: {{ school.teachers_count }}</div>
                                    <div>{{ $tc('messages.students', 2) }}: {{ school.students_count }}</div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton
                                        action="activate"
                                        :href="school.links.activate"
                                        v-if="school.links.activate"
                                    />
                                    <InertiaSquareLinkButton action="show" :href="school.links.show" />
                                    <InertiaSquareLinkButton action="edit" :href="school.links.edit" />
                                    <InertiaSquareLinkButton
                                        action="impersonate"
                                        :href="school.links.impersonate"
                                        v-if="school.links.impersonate"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="schools" />
        </template>
    </PanelPage>
</template>

<script>
import CentralLayout from '@/Shared/CentralLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import { Tippy } from 'vue-tippy';
import AppButton from '@/components/AppButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        AppButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        Tippy,
    },
    layout: CentralLayout,
    name: 'Index',
    props: ['meta', 'schools', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
